/**
 * /team-roster — view, edit, add, and offboard team members (Sep 9, 2026).
 *
 * Greg: "i want the team roster to allow us to make edits, add or remove team
 * members." Gated to admin / sales_director / director_of_operations (+ super
 * users) — the same tier as /team-boards — and, since Oct 19, 2026, finance
 * (Greg: "shannon can edit the roster"; lib/adminAccess.ts). Server side, the
 * matching `tr_leadership_*` RLS policies + a sensitive-column guard trigger
 * enforce it (both admit finance since 20261019g), and every change lands in
 * `team_roster_audit` via trigger (shown at the bottom of the page).
 *
 * Rules this page enforces because getting them wrong silently moves money:
 *   • "Remove" = DEACTIVATE, never delete. Deals, commissions, and history all
 *     hang off the roster row — offboarding flips is_active + stamps
 *     departure_date, and the confirm shows what the person still holds.
 *   • A team_lead never carries a team_lead_id of their own (TLs roll up via
 *     asm_id). A non-null value would silently pay that person a 1% override
 *     on the TL's personal deals (the Rob Mason bug, Aug 21).
 *   • Standing rule (Aug 21): every active RM/TL rolls up to the ASM unless
 *     noted — the add flow pre-fills asm_id when there's exactly one active ASM.
 *   • commission_rate is the RM's ACHIEVED personal rate (rmRateFor); ≥12%
 *     pays flat on new-structure deals. Rates live in the leadership-only
 *     `team_roster_comp` table (Sep 9, 2026) — this page reads and writes
 *     THAT; the readable team_roster column is neutralized to the 0.10 base.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { icSigningLink, sendIcSigningEmail } from "../lib/icOnboardingEmail";
import { currentWeekStart, fetchWeeklyGoalsMap, setWeeklyGoal } from "../components/crew/crewQueries";
import { monthStartOf, todayET } from "../lib/dailyCommitments";
import { BusinessCardButton } from "../components/business-card/BusinessCardButton";
import { canEditRoster } from "../lib/adminAccess";
import { useTabParam } from "../lib/useTabParam";
import {
  buildOrgTree, descendantIds, LINE_BY_PAY_ROLES, ROLE_LABEL, ROLE_ORDER, suggestedLines,
} from "../lib/orgChart";
import OrgChart from "../components/roster/OrgChart";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

// Roles whose personal commission_rate the money engine reads (rmRateFor).
const SALES_RATE_ROLES = new Set(["restore_manager", "team_lead", "asm"]);

// Roles where the add flow defaults "send the IC agreement" ON.
const IC_LINK_DEFAULT_ROLES = new Set([
  "restore_manager", "team_lead", "canvasser", "roof_specialist", "project_manager",
]);

// Everything except closed_won + the six re-canvassable dead stages = "live".
const TERMINAL_STAGES =
  "(closed_won,closed_lost,cancelled,no_thanks,insufficient_damage,claims_denied,closed_inspection)";

// commission_rate is NOT selected here — real rates live in team_roster_comp
// (leadership-only, Sep 9, 2026) and get merged in below; the roster column is
// neutralized to the 0.10 base.
const ROSTER_SELECT =
  "id,full_name,email,phone,address,role,secondary_role,team_lead_id,asm_id,reports_to_id,is_active,on_leave,on_leave_since,on_leave_note,hire_date,departure_date,bridge_sms_enabled,crew_bridge_enabled,onboarding_complete,ic_agreement_signed_date,created_at";

const FIELD_LABEL: Record<string, string> = {
  full_name: "Name",
  email: "Email",
  phone: "Phone",
  role: "Role",
  secondary_role: "2nd role",
  commission_rate: "Commission rate",
  team_lead_id: "Team lead",
  asm_id: "ASM",
  reports_to_id: "Reports to",
  is_active: "Active",
  on_leave: "On leave",
  hire_date: "Hire date",
  departure_date: "Departure date",
  bridge_sms_enabled: "Team-line SMS",
  crew_bridge_enabled: "Crew-line SMS",
};

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface RosterRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  role: string;
  secondary_role: string | null;
  team_lead_id: string | null;
  asm_id: string | null;
  // Org chart only (20261027i) — never read for pay.
  reports_to_id: string | null;
  // PostgREST returns numerics as strings — always run through Number().
  commission_rate: number | string | null;
  is_active: boolean;
  on_leave: boolean;
  on_leave_since: string | null;
  on_leave_note: string | null;
  hire_date: string | null;
  departure_date: string | null;
  bridge_sms_enabled: boolean;
  crew_bridge_enabled: boolean;
  onboarding_complete: boolean;
  ic_agreement_signed_date: string | null;
  created_at: string;
}

interface AuditRow {
  id: string;
  member_id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: "created" | "updated" | "deactivated" | "reactivated";
  changes: Record<string, { from?: unknown; to?: unknown }>;
  created_at: string;
}

interface DealLoad { own: number; split: number; unpaidComm: number; }

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

function ratePct(v: number | string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 1000) / 10 : null;
}

// hire_date / departure_date are DATE columns → bare ISO strings; never run a
// date-only value through new Date() without a time (the May 9 off-by-one).
function fmtDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function TeamRoster() {
  const { member, isSuperUser } = useAuth();
  const canUse = canEditRoster(member?.role, isSuperUser);

  const [rows, setRows] = useState<RosterRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loads, setLoads] = useState<Map<string, DealLoad>>(new Map());
  const [loadsFailed, setLoadsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<RosterRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useTabParam<"list" | "chart">("view", "list", ["list", "chart"]);

  const runSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++runSeqRef.current;
    const stale = () => runSeqRef.current !== seq;
    try {
      const [rosterRes, compRes, auditRes] = await Promise.all([
        supabase.from("team_roster").select(ROSTER_SELECT).order("full_name"),
        // Leadership reads the comp table directly (RLS-gated); a failure is
        // fatal — this board must never show 10% for someone who isn't.
        supabase.from("team_roster_comp").select("member_id,commission_rate"),
        supabase
          .from("team_roster_audit")
          .select("id,member_id,actor_id,actor_email,action,changes,created_at")
          .order("created_at", { ascending: false })
          .limit(25),
      ]);
      if (stale()) return;
      if (rosterRes.error) throw rosterRes.error;
      if (compRes.error) throw compRes.error;
      const rateById = new Map<string, number>();
      for (const c of (compRes.data ?? []) as Array<{ member_id: string; commission_rate: number | string }>) {
        const n = Number(c.commission_rate);
        if (Number.isFinite(n)) rateById.set(c.member_id, n);
      }
      const merged = ((rosterRes.data ?? []) as RosterRow[]).map((r) => ({
        ...r,
        commission_rate: rateById.get(r.id) ?? 0.1,
      }));
      setRows(merged);
      setAudit(auditRes.error ? [] : ((auditRes.data ?? []) as AuditRow[]));
      setErr(null);

      // Per-member deal load (best-effort — a failed read hides the counts
      // with a note; it must never render as a confident zero).
      try {
        const map = new Map<string, DealLoad>();
        const bump = (id: string | null, key: keyof DealLoad) => {
          if (!id) return;
          const cur = map.get(id) ?? { own: 0, split: 0, unpaidComm: 0 };
          cur[key] += 1;
          map.set(id, cur);
        };
        const PAGE = 1000;
        for (let from = 0; ; from += PAGE) {
          const { data, error } = await supabase
            .from("deals")
            .select("id,owner_id,split_rm_id")
            .not("stage", "in", TERMINAL_STAGES)
            .order("id")
            .range(from, from + PAGE - 1);
          if (error) throw error;
          const chunk = (data ?? []) as Array<{ owner_id: string | null; split_rm_id: string | null }>;
          for (const d of chunk) {
            bump(d.owner_id, "own");
            if (d.split_rm_id && d.split_rm_id !== d.owner_id) bump(d.split_rm_id, "split");
          }
          if (chunk.length < PAGE) break;
        }
        for (let from = 0; ; from += PAGE) {
          const { data, error } = await supabase
            .from("deals")
            .select("id,owner_id,split_rm_id")
            .not("commission_ready_date", "is", null)
            .eq("commission_paid", false)
            .order("id")
            .range(from, from + PAGE - 1);
          if (error) throw error;
          const chunk = (data ?? []) as Array<{ owner_id: string | null; split_rm_id: string | null }>;
          for (const d of chunk) {
            bump(d.owner_id, "unpaidComm");
            if (d.split_rm_id && d.split_rm_id !== d.owner_id) bump(d.split_rm_id, "unpaidComm");
          }
          if (chunk.length < PAGE) break;
        }
        if (stale()) return;
        setLoads(map);
        setLoadsFailed(false);
      } catch {
        if (!stale()) setLoadsFailed(true);
      }
    } catch (e) {
      if (!stale()) setErr(e instanceof Error ? e.message : "Couldn't load the roster.");
    } finally {
      if (!stale()) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("team_roster", load);
  useRealtimeTable("team_roster_comp", load);

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const matches = useCallback((r: RosterRow) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.full_name.toLowerCase().includes(q) ||
      (r.email ?? "").toLowerCase().includes(q) ||
      (r.phone ?? "").replace(/\D/g, "").includes(q.replace(/\D/g, "") || " ")
    );
  }, [search]);

  const activeGroups = useMemo(() => {
    const act = rows.filter((r) => r.is_active && !r.on_leave && matches(r));
    const known = new Map<string, RosterRow[]>();
    for (const role of ROLE_ORDER) known.set(role, []);
    const extra = new Map<string, RosterRow[]>();
    for (const r of act) {
      const bucket = known.get(r.role) ?? extra.get(r.role);
      if (bucket) bucket.push(r);
      else extra.set(r.role, [r]);
    }
    const out: Array<{ role: string; members: RosterRow[] }> = [];
    for (const role of ROLE_ORDER) {
      const m = known.get(role)!;
      if (m.length) out.push({ role, members: m });
    }
    for (const [role, m] of extra) out.push({ role, members: m });
    return out;
  }, [rows, matches]);

  const onLeaveRows = useMemo(
    () => rows.filter((r) => r.is_active && r.on_leave && matches(r)),
    [rows, matches],
  );

  const inactiveRows = useMemo(
    () => rows.filter((r) => !r.is_active && matches(r)),
    [rows, matches],
  );

  const activeCount = rows.filter((r) => r.is_active && !r.on_leave).length;
  const onLeaveCount = rows.filter((r) => r.is_active && r.on_leave).length;

  if (!canUse) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-600">
          The Team Roster board is limited to leadership and finance.
        </div>
      </div>
    );
  }

  return (
    <div className={`${view === "chart" ? "max-w-7xl" : "max-w-5xl"} mx-auto p-4 sm:p-6 space-y-5`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-bardin-ink">👥 Team Roster</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeCount} active{onLeaveCount ? ` · ${onLeaveCount} on leave` : ""} · {rows.length - activeCount - onLeaveCount} inactive — edit anyone, add a new member, or offboard someone.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="px-4 py-2.5 rounded-lg bg-bardin-orange text-white text-sm font-semibold hover:bg-bardin-orange/90 transition"
        >
          + Add team member
        </button>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {err} <button onClick={() => void load()} className="underline font-semibold ml-1">Retry</button>
        </div>
      )}

      {/* View switch + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5" role="tablist" aria-label="Roster view">
          {([["list", "☰ List"], ["chart", "🗂 Org chart"]] as const).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={`px-3 py-2 rounded-md text-sm font-semibold transition ${view === key ? "bg-bardin-ink text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={view === "chart" ? "Find someone on the chart…" : "Search name, email, or phone…"}
          className="w-full sm:w-80 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
        />
      </div>

      {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading the roster…</div>}

      {/* Org chart (Oct 27, 2026) — tap a person to open the same editor. */}
      {!loading && view === "chart" && (
        <OrgChart
          members={rows}
          search={search}
          onOpen={(id) => { const r = byId.get(id); if (r) setEditing(r); }}
        />
      )}

      {/* Active members grouped by role */}
      {!loading && view === "list" && activeGroups.map((g) => (
        <section key={g.role}>
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            {ROLE_LABEL[g.role] ?? g.role} · {g.members.length}
          </h2>
          <div className="space-y-2">
            {g.members.map((r) => (
              <MemberRow
                key={r.id}
                r={r}
                byId={byId}
                load={loadsFailed ? null : (loads.get(r.id) ?? { own: 0, split: 0, unpaidComm: 0 })}
                onEdit={() => setEditing(r)}
              />
            ))}
          </div>
        </section>
      ))}

      {!loading && view === "list" && activeGroups.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center">No active members match "{search}".</div>
      )}

      {loadsFailed && !loading && view === "list" && (
        <p className="text-[11px] text-amber-600">⚠ Deal counts couldn't load — the chips are hidden rather than showing zeros.</p>
      )}

      {/* On leave — still active for deals + split pay; out of goals / daily plans / ranking */}
      {!loading && view === "list" && onLeaveRows.length > 0 && (
        <section className="bg-sky-50/60 border border-sky-200 rounded-xl p-4">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-sky-700 mb-1">On leave · {onLeaveRows.length}</h2>
          <p className="text-[11px] text-sky-800/80 mb-3">
            Out of goals, daily plans, the 1-on-1 list and leaderboard ranking. Their deals still close in their name and split commissions still pay.
          </p>
          <div className="space-y-2">
            {onLeaveRows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-bardin-ink">{r.full_name}</div>
                  <div className="text-[11px] text-slate-500">
                    {ROLE_LABEL[r.role] ?? r.role}
                    {r.on_leave_since ? ` · since ${fmtDay(r.on_leave_since)}` : ""}
                    {r.on_leave_note ? ` · ${r.on_leave_note}` : ""}
                    {!loadsFailed && loads.get(r.id) && (loads.get(r.id)!.own > 0 || loads.get(r.id)!.split > 0)
                      ? ` · ${loads.get(r.id)!.own} live${loads.get(r.id)!.split ? ` +${loads.get(r.id)!.split} split` : ""}`
                      : ""}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <BusinessCardButton member={r} />
                  <button
                    onClick={() => setEditing(r)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    ✎ Edit / bring back
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Inactive */}
      {!loading && view === "list" && inactiveRows.length > 0 && (
        <details className="bg-slate-50 border border-slate-200 rounded-xl">
          <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-600 select-none">
            Inactive / offboarded · {inactiveRows.length}
          </summary>
          <div className="px-4 pb-4 space-y-2">
            {inactiveRows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-600">{r.full_name}</div>
                  <div className="text-[11px] text-slate-400">
                    {ROLE_LABEL[r.role] ?? r.role}
                    {r.departure_date ? ` · left ${fmtDay(r.departure_date)}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(r)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  ✎ Edit / reactivate
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Recent changes (team_roster_audit) */}
      {!loading && audit.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="text-sm font-bold text-bardin-ink mb-3">Recent changes</h2>
          <div className="space-y-2">
            {audit.map((a) => (
              <AuditLine key={a.id} a={a} byId={byId} />
            ))}
          </div>
        </section>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Removing someone <span className="font-semibold">deactivates</span> them — their deals, commission records, and
        history stay intact, and they can be reactivated later. Reassign their live deals from the deal page (Admin
        Controls). Every change on this board is recorded above.
      </p>

      {(editing || adding) && (
        <MemberEditorModal
          mode={adding ? "add" : "edit"}
          row={adding ? null : editing}
          roster={rows}
          load={editing ? (loads.get(editing.id) ?? { own: 0, split: 0, unpaidComm: 0 }) : null}
          loadsKnown={!loadsFailed}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={() => void load()}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Member row                                                          */
/* ------------------------------------------------------------------ */

function MemberRow({ r, byId, load, onEdit }: {
  r: RosterRow;
  byId: Map<string, RosterRow>;
  load: DealLoad | null;
  onEdit: () => void;
}) {
  const pct = ratePct(r.commission_rate);
  const showRate = SALES_RATE_ROLES.has(r.role);
  const tl = r.team_lead_id ? byId.get(r.team_lead_id) : null;
  const asm = r.asm_id ? byId.get(r.asm_id) : null;
  const boss = !LINE_BY_PAY_ROLES.has(r.role) && r.reports_to_id ? byId.get(r.reports_to_id) : null;
  const needsAsm = (r.role === "restore_manager" || r.role === "team_lead") && !r.asm_id;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-white border border-slate-200 rounded-xl px-4 py-3">
      <div className="min-w-0 flex-1 basis-48">
        <div className="text-sm font-bold text-bardin-ink truncate">{r.full_name}</div>
        <div className="text-[11px] text-slate-400 truncate">{r.email ?? "no email"}{r.phone ? ` · ${r.phone}` : ""}</div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {r.secondary_role && (
          <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
            +{ROLE_LABEL[r.secondary_role] ?? r.secondary_role}
          </span>
        )}
        {showRate && pct != null && (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${pct >= 12 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
            {pct}%{pct >= 12 ? " · achieved" : ""}
          </span>
        )}
        {tl && r.role === "restore_manager" && (
          <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[11px]">TL {tl.full_name.split(" ")[0]}</span>
        )}
        {asm && (
          <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[11px]">ASM {asm.full_name.split(" ")[0]}</span>
        )}
        {boss && (
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">Reports to {boss.full_name.split(" ")[0]}</span>
        )}
        {needsAsm && (
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold" title="Standing rule: every active RM/TL rolls up to the ASM unless noted.">⚠ no ASM</span>
        )}
        {load && (load.own > 0 || load.split > 0) && (
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">
            {load.own} live{load.split ? ` +${load.split} split` : ""}
          </span>
        )}
        {load && load.unpaidComm > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[11px]" title="Deals in the commission queue where this person has an unpaid share.">
            {load.unpaidComm} comm unpaid
          </span>
        )}
      </div>
      <div className="ml-auto flex items-start gap-2">
        <BusinessCardButton member={r} />
        <button
          onClick={onEdit}
          className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          ✎ Edit
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Audit line                                                          */
/* ------------------------------------------------------------------ */

function fmtChangeVal(key: string, v: unknown, byId: Map<string, RosterRow>): string {
  if (v == null) return "—";
  if (key === "commission_rate") {
    const p = ratePct(v as number);
    return p == null ? String(v) : `${p}%`;
  }
  if (key.endsWith("_id")) {
    const m = byId.get(String(v));
    return m ? m.full_name : String(v).slice(0, 8);
  }
  if (typeof v === "boolean") return v ? "on" : "off";
  return String(v);
}

function AuditLine({ a, byId }: { a: AuditRow; byId: Map<string, RosterRow> }) {
  const who = (a.actor_id && byId.get(a.actor_id)?.full_name) || a.actor_email || "System";
  const target = byId.get(a.member_id)?.full_name ?? "a former member";
  let detail: string;
  if (a.action === "created") {
    const role = a.changes.role?.to;
    detail = `added to the roster${role ? ` (${ROLE_LABEL[String(role)] ?? String(role)})` : ""}`;
  } else if (a.action === "deactivated") {
    detail = "offboarded (deactivated)";
  } else if (a.action === "reactivated") {
    detail = "reactivated";
  } else {
    detail = Object.entries(a.changes)
      .map(([k, v]) => `${FIELD_LABEL[k] ?? k}: ${fmtChangeVal(k, v.from, byId)} → ${fmtChangeVal(k, v.to, byId)}`)
      .join(" · ");
  }
  return (
    <div className="text-xs text-slate-600 leading-relaxed">
      <span className="font-semibold text-bardin-ink">{who}</span>
      {" · "}
      <span className="font-semibold">{target}</span>{" — "}{detail}
      <span className="text-slate-400"> · {new Date(a.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor modal (add + edit + offboard + reactivate)                   */
/* ------------------------------------------------------------------ */

function MemberEditorModal({ mode, row, roster, load, loadsKnown, onClose, onSaved }: {
  mode: "add" | "edit";
  row: RosterRow | null;
  roster: RosterRow[];
  load: DealLoad | null;
  loadsKnown: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const activeTLs = roster.filter((m) => m.is_active && m.role === "team_lead");
  const activeASMs = roster.filter((m) => m.is_active && m.role === "asm");
  // Starting lines for a new hire (lib/orgChart.ts): the one active TL + ASM
  // for sales (standing rules Aug 21 + Sep 15), the usual boss otherwise.
  const startLines = mode === "add" ? suggestedLines(row?.role ?? "restore_manager", roster) : null;

  const [name, setName] = useState(row?.full_name ?? "");
  const [email, setEmail] = useState(row?.email ?? "");
  const [phone, setPhone] = useState(row?.phone ?? "");
  const [address, setAddress] = useState(row?.address ?? "");
  const [role, setRole] = useState(row?.role ?? "restore_manager");
  const [secondaryRole, setSecondaryRole] = useState(row?.secondary_role ?? "");
  const [teamLeadId, setTeamLeadId] = useState(row?.team_lead_id ?? startLines?.team_lead_id ?? "");
  const [asmId, setAsmId] = useState(row?.asm_id ?? startLines?.asm_id ?? "");
  const [reportsToId, setReportsToId] = useState(row?.reports_to_id ?? startLines?.reports_to_id ?? "");
  // Once someone picks a line by hand, a role change stops re-deriving them.
  const [linesTouched, setLinesTouched] = useState(false);
  const [rateStr, setRateStr] = useState(() => {
    const p = ratePct(row?.commission_rate ?? (mode === "add" ? 0.1 : null));
    return p == null ? "" : String(p);
  });
  const [hireDate, setHireDate] = useState(row?.hire_date ?? (mode === "add" ? todayISO() : ""));
  const [bridgeSms, setBridgeSms] = useState(row?.bridge_sms_enabled ?? true);
  const [crewBridge, setCrewBridge] = useState(row?.crew_bridge_enabled ?? true);
  const [sendIc, setSendIc] = useState(mode === "add" && IC_LINK_DEFAULT_ROLES.has(row?.role ?? "restore_manager"));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveNote, setLeaveNote] = useState("");
  const [success, setSuccess] = useState<{ link: string; emailed: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const isSales = SALES_RATE_ROLES.has(role);
  const showTl = role === "restore_manager";
  const showAsm = role === "restore_manager" || role === "team_lead";
  const emailChanged = mode === "edit" && (row?.email ?? "") !== email.trim().toLowerCase() && email.trim() !== (row?.email ?? "");

  // RM and TL rows sit on the org chart by team lead / ASM; everyone else by
  // the display-only "Reports to" (20261027i). Never offer the person
  // themselves or anyone under them on the chart (that would be a loop).
  const showReportsTo = !LINE_BY_PAY_ROLES.has(role);
  const bossOptions = useMemo(() => {
    const blocked = row ? descendantIds(buildOrgTree(roster), row.id) : new Set<string>();
    if (row) blocked.add(row.id);
    return roster
      .filter((m) => m.is_active && !blocked.has(m.id))
      .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.full_name.localeCompare(b.full_name));
  }, [roster, row]);
  const currentBoss = reportsToId ? roster.find((m) => m.id === reportsToId) ?? null : null;

  // Role changes mid-add re-derive the defaults: the IC-link checkbox (field
  // roles sign one, office staff don't) and the starting lines (the one TL +
  // ASM for sales — standing rule — and the usual boss for other roles).
  useEffect(() => {
    if (mode !== "add") return;
    setSendIc(IC_LINK_DEFAULT_ROLES.has(role));
    if (linesTouched) return;
    const lines = suggestedLines(role, roster);
    setTeamLeadId(lines.team_lead_id ?? "");
    setAsmId(lines.asm_id ?? "");
    setReportsToId(lines.reports_to_id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // The rate is returned separately — it writes to team_roster_comp (the
  // leadership-only rates table, Sep 9, 2026), never to team_roster.
  const buildTarget = (): { target: Record<string, unknown>; rateNum: number | null; error?: string } => {
    const nm = name.trim();
    const em = email.trim().toLowerCase();
    if (!nm) return { target: {}, rateNum: null, error: "Full name is required." };
    if (!em || !em.includes("@")) return { target: {}, rateNum: null, error: "A valid email is required — it's how they sign in." };
    const target: Record<string, unknown> = {
      full_name: nm,
      email: em,
      phone: phone.trim() || null,
      address: address.trim() || null,
      role,
      secondary_role: secondaryRole || null,
      team_lead_id: role === "restore_manager" ? (teamLeadId || null) : null,
      asm_id: showAsm ? (asmId || null) : null,
      // RM/TL rows keep whatever is stored (the chart reads their TL/ASM).
      reports_to_id: showReportsTo ? (reportsToId || null) : (row?.reports_to_id ?? null),
      hire_date: hireDate || null,
      bridge_sms_enabled: bridgeSms,
      crew_bridge_enabled: crewBridge,
    };
    let rateNum: number | null = null;
    if (isSales) {
      const p = Number(rateStr);
      if (!Number.isFinite(p) || p <= 0 || p > 50) {
        return { target: {}, rateNum: null, error: "Commission rate looks wrong — enter a percent like 10 or 12." };
      }
      rateNum = Math.round(p * 100) / 10000;
    }
    return { target, rateNum };
  };

  const handleSave = async () => {
    const { target, rateNum, error } = buildTarget();
    if (error) { setErr(error); return; }
    setSaving(true);
    setErr(null);
    try {
      if (mode === "add") {
        const token = sendIc ? crypto.randomUUID() : null;
        const payload: Record<string, unknown> = {
          ...target,
          is_active: true,
          onboarding_complete: !sendIc,
          ...(token ? { ic_onboarding_token: token } : {}),
        };
        const { data: ins, error: insErr } = await supabase
          .from("team_roster").insert(payload).select("id").single();
        if (insErr) {
          if (insErr.message.includes("duplicate") || insErr.message.includes("unique")) {
            throw new Error("A team member with this email already exists (check the inactive list).");
          }
          throw insErr;
        }
        if (rateNum != null && ins?.id) {
          const { error: rateErr } = await supabase
            .from("team_roster_comp")
            .upsert({ member_id: ins.id, commission_rate: rateNum }, { onConflict: "member_id" });
          if (rateErr) throw new Error(`Member added, but the rate didn't save: ${rateErr.message}`);
        }
        onSaved();
        if (token) {
          const link = icSigningLink(token);
          let emailed = true;
          try { await sendIcSigningEmail(String(target.email), String(target.full_name), link); }
          catch { emailed = false; }
          setSuccess({ link, emailed });
        } else {
          onClose();
        }
      } else if (row) {
        const patch: Record<string, unknown> = {};
        const rowAny = row as unknown as Record<string, unknown>;
        for (const [k, v] of Object.entries(target)) {
          if ((rowAny[k] ?? null) !== (v ?? null)) patch[k] = v;
        }
        const rateChanged = rateNum != null && Number(row.commission_rate ?? 0.1) !== rateNum;
        if (Object.keys(patch).length === 0 && !rateChanged) { onClose(); return; }
        if (Object.keys(patch).length > 0) {
          const { error: upErr } = await supabase.from("team_roster").update(patch).eq("id", row.id);
          if (upErr) throw upErr;
        }
        if (rateChanged) {
          const { error: rateErr } = await supabase
            .from("team_roster_comp")
            .upsert({ member_id: row.id, commission_rate: rateNum }, { onConflict: "member_id" });
          if (rateErr) throw new Error(`Saved, but the rate didn't: ${rateErr.message}`);
        }
        onSaved();
        onClose();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed — try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleOffboard = async () => {
    if (!row) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from("team_roster")
      .update({ is_active: false, departure_date: todayISO() })
      .eq("id", row.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
    onClose();
  };

  // On leave: stays active (deals + split pay keep working), drops out of goals,
  // daily plans, the 1-on-1 rail and leaderboard ranking. Their goals from this
  // week / month forward are cleared so the company totals stop counting them.
  const handlePutOnLeave = async () => {
    if (!row) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from("team_roster")
      .update({ on_leave: true, on_leave_since: todayISO(), on_leave_note: leaveNote.trim() || null })
      .eq("id", row.id);
    if (error) { setSaving(false); setErr(error.message); return; }
    try {
      const weekStart = currentWeekStart();
      const monthStart = monthStartOf(todayET());
      await supabase.from("crew_goals").delete()
        .eq("scope", "personal").eq("user_id", row.id).eq("period", "week").gte("period_start", weekStart);
      await supabase.from("crew_goals").delete()
        .eq("scope", "personal").eq("user_id", row.id).eq("period", "month").gte("period_start", monthStart);
      // Re-sync this week's company row = Σ personal (canvassers + on-leave excluded),
      // the same rule the ✏️ Set goals drawer uses.
      const [gm, excl] = await Promise.all([
        fetchWeeklyGoalsMap(weekStart),
        supabase.from("team_roster").select("id").or("role.eq.canvasser,on_leave.eq.true"),
      ]);
      const skip = new Set(((excl.data ?? []) as Array<{ id: string }>).map((x) => x.id));
      skip.add(row.id);
      const { data: me } = await supabase.auth.getUser();
      const creator = roster.find((m) => (m.email ?? "").toLowerCase() === (me.user?.email ?? "").toLowerCase())?.id;
      if (gm["company"] && creator) {
        for (const metric of ["apps", "claims", "builds_scheduled", "builds"] as const) {
          let sum = 0;
          for (const [k, g] of Object.entries(gm)) {
            if (k === "company" || skip.has(k)) continue;
            sum += Number(g[metric] ?? 0);
          }
          if (sum !== Number(gm["company"][metric] ?? 0)) {
            await setWeeklyGoal({ userId: null, metric, target: sum, weekStart, createdBy: creator });
          }
        }
      }
    } catch (e) {
      console.warn("[TeamRoster] clearing goals for leave failed:", e);
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  const handleBackFromLeave = async () => {
    if (!row) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from("team_roster")
      .update({ on_leave: false, on_leave_since: null, on_leave_note: null })
      .eq("id", row.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
    onClose();
  };

  const handleReactivate = async () => {
    if (!row) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from("team_roster")
      .update({ is_active: true, departure_date: null })
      .eq("id", row.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
    onClose();
  };

  const handleCopy = async () => {
    if (!success) return;
    try {
      await navigator.clipboard.writeText(success.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent";
  const labelCls = "block text-xs font-semibold text-bardin-ink mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-bardin-ink">
              {mode === "add" ? "Add team member" : `Edit ${row?.full_name ?? ""}`}
            </h2>
            {mode === "edit" && row && row.is_active && row.on_leave && (
              <p className="text-xs text-sky-700 mt-0.5 font-semibold">On leave{row.on_leave_since ? ` since ${fmtDay(row.on_leave_since)}` : ""}{row.on_leave_note ? ` · ${row.on_leave_note}` : ""}</p>
            )}
            {mode === "edit" && row && !row.is_active && (
              <p className="text-xs text-amber-600 mt-0.5 font-semibold">Inactive{row.departure_date ? ` since ${fmtDay(row.departure_date)}` : ""}</p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        {success ? (
          <div className="p-5 space-y-4">
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-bold text-bardin-ink">{name.trim()} added!</h3>
              <p className="text-sm text-slate-500 mt-1">
                {success.emailed
                  ? "The IC agreement signing link was emailed to them. You can also share it directly:"
                  : "⚠ The email didn't go out — share this signing link with them directly:"}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 break-all text-xs text-slate-600 font-mono">
              {success.link}
            </div>
            <div className="flex gap-3">
              <button onClick={handleCopy} className="flex-1 py-2.5 rounded-lg bg-bardin-teal text-white text-sm font-semibold hover:bg-bardin-teal/90 transition">
                {copied ? "Copied!" : "Copy link"}
              </button>
              <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Full name <span className="text-bardin-orange">*</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} autoFocus={mode === "add"} />
            </div>

            <div>
              <label className={labelCls}>Email <span className="text-bardin-orange">*</span></label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@bardinroofing.com" className={inputCls} />
              <p className="text-[11px] text-slate-400 mt-1">
                They sign in with Google using this address — the Workspace account must exist.
              </p>
              {emailChanged && (
                <p className="text-[11px] text-amber-600 mt-1 font-semibold">
                  ⚠ Changing the email changes who can log in as this person.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Phone</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(770) 555-1234" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Hire date</label>
                <input type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} className={inputCls} />
              </div>
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">
              US 10-digit cell powers call/text bridging — an international number can't bridge (browser softphone only).
            </p>

            <div>
              <label className={labelCls}>Address</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, Canton, GA 30114" className={inputCls} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className={`${inputCls} bg-white`}>
                  {ROLE_ORDER.map((v) => (
                    <option key={v} value={v}>{ROLE_LABEL[v] ?? v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Secondary role</label>
                <select value={secondaryRole} onChange={(e) => setSecondaryRole(e.target.value)} className={`${inputCls} bg-white`}>
                  <option value="">None</option>
                  {ROLE_ORDER.map((v) => (
                    <option key={v} value={v}>{ROLE_LABEL[v] ?? v}</option>
                  ))}
                </select>
              </div>
            </div>
            {mode === "edit" && row && role !== row.role && (
              <p className="text-[11px] text-amber-600 -mt-2">
                Changing the role changes their landing page, home board, and which tools they can open.
              </p>
            )}

            {isSales && (
              <div>
                <label className={labelCls}>Personal commission rate (%)</label>
                <input type="number" step="0.5" min="1" max="50" value={rateStr} onChange={(e) => setRateStr(e.target.value)} className={inputCls} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Base is 10. An achieved rate of 12%+ pays flat on their own deals under the current structure.
                </p>
              </div>
            )}

            {(showTl || showAsm) && (
              <div className="grid grid-cols-2 gap-3">
                {showTl && (
                  <div>
                    <label className={labelCls}>Team lead</label>
                    <select value={teamLeadId} onChange={(e) => { setTeamLeadId(e.target.value); setLinesTouched(true); }} className={`${inputCls} bg-white`}>
                      <option value="">None</option>
                      {activeTLs.map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showAsm && (
                  <div>
                    <label className={labelCls}>ASM</label>
                    <select value={asmId} onChange={(e) => { setAsmId(e.target.value); setLinesTouched(true); }} className={`${inputCls} bg-white`}>
                      <option value="">None</option>
                      {activeASMs.map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
            {role === "team_lead" && (
              <p className="text-[11px] text-slate-400 -mt-2">
                Team leads carry no team lead of their own — they roll up through their ASM. (Setting one would wrongly
                pay a 1% override on this person's personal deals.)
              </p>
            )}
            {showAsm && !asmId && (
              <p className="text-[11px] text-amber-600 -mt-2 font-semibold">
                ⚠ Standing rule: every active RM/TL rolls up to the ASM unless noted.
              </p>
            )}

            {showReportsTo ? (
              <div>
                <label className={labelCls}>Reports to</label>
                <select value={reportsToId} onChange={(e) => { setReportsToId(e.target.value); setLinesTouched(true); }} className={`${inputCls} bg-white`}>
                  <option value="">No one</option>
                  {currentBoss && !bossOptions.some((m) => m.id === currentBoss.id) && (
                    <option value={currentBoss.id}>
                      {currentBoss.full_name}{!currentBoss.is_active ? " (inactive)" : " (reports to them — loop)"}
                    </option>
                  )}
                  {bossOptions.map((m) => (
                    <option key={m.id} value={m.id}>{m.full_name} · {ROLE_LABEL[m.role] ?? m.role}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Places them on the org chart. It never changes pay.
                  {currentBoss && !currentBoss.is_active ? " Their current boss is inactive — pick someone active." : ""}
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-slate-400 -mt-2">
                On the org chart, {role === "team_lead" ? "a team lead sits under their ASM" : "an RM sits under their team lead (or the ASM if none)"}.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <input type="checkbox" checked={bridgeSms} onChange={(e) => setBridgeSms(e.target.checked)} className="w-4 h-4 accent-bardin-orange" />
                Team-line SMS alerts
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <input type="checkbox" checked={crewBridge} onChange={(e) => setCrewBridge(e.target.checked)} className="w-4 h-4 accent-bardin-orange" />
                Crew-line broadcasts
              </label>
            </div>

            {mode === "add" && (
              <label className="flex items-start gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <input type="checkbox" checked={sendIc} onChange={(e) => setSendIc(e.target.checked)} className="w-4 h-4 mt-0.5 accent-bardin-orange" />
                <span>
                  <span className="font-semibold">Send the IC agreement signing link</span> by email (same flow as
                  Onboard IC). Uncheck for W-2 / office staff who don't sign one.
                </span>
              </label>
            )}

            {mode === "edit" && row && !row.onboarding_complete && !row.ic_agreement_signed_date && (
              <p className="text-[11px] text-amber-600">⚠ IC agreement not signed yet.</p>
            )}

            {err && <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2">{err}</div>}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition ${saving ? "bg-slate-300 cursor-not-allowed" : "bg-bardin-orange hover:bg-bardin-orange/90"}`}
              >
                {saving ? "Saving…" : mode === "add" ? "Add member" : "Save changes"}
              </button>
            </div>

            {/* On leave (college, etc.) — not offboarding */}
            {mode === "edit" && row && row.is_active && row.on_leave && (
              <div className="border-t border-slate-200 pt-4 mt-2">
                <button
                  onClick={handleBackFromLeave}
                  disabled={saving}
                  className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  ↩ {row.full_name.split(" ")[0]} is back — end leave
                </button>
                <p className="text-[11px] text-slate-500 mt-1.5">They reappear in goals, daily plans and the 1-on-1 list. Set their goals in ✏️ Set goals on the Crew board.</p>
              </div>
            )}
            {mode === "edit" && row && row.is_active && !row.on_leave && (
              <div className="border-t border-slate-200 pt-4 mt-2">
                {!leaveOpen ? (
                  <button onClick={() => setLeaveOpen(true)} className="text-xs font-semibold text-sky-700 hover:text-sky-800">
                    Put {row.full_name.split(" ")[0]} on leave (college, time off)…
                  </button>
                ) : (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-bold text-sky-800">Put {row.full_name} on leave?</p>
                    <p className="text-[11px] text-sky-800 leading-relaxed">
                      They stay on the roster and can still log in. Their deals keep closing in their name, split
                      commissions still pay, and activity from a deal someone else closes still credits them. They come
                      off weekly / monthly goals (this week forward is cleared), daily plans, the 1-on-1 list, Call List
                      assignment and leaderboard ranking (they only show there when they earn points).
                    </p>
                    <input
                      value={leaveNote}
                      onChange={(e) => setLeaveNote(e.target.value)}
                      placeholder="Note (optional) — e.g. back in May after semester"
                      className={inputCls}
                    />
                    <div className="flex gap-2">
                      <button onClick={() => setLeaveOpen(false)} className="flex-1 py-2 rounded-lg bg-white border border-slate-300 text-xs font-semibold text-slate-600">
                        Cancel
                      </button>
                      <button onClick={handlePutOnLeave} disabled={saving} className="flex-1 py-2 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-50">
                        Put on leave
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Danger zone / reactivate */}
            {mode === "edit" && row && row.is_active && (
              <div className="border-t border-slate-200 pt-4 mt-2">
                {!offboardOpen ? (
                  <button onClick={() => setOffboardOpen(true)} className="text-xs font-semibold text-red-600 hover:text-red-700">
                    Offboard {row.full_name.split(" ")[0]} (deactivate)…
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-bold text-red-700">Offboard {row.full_name}?</p>
                    <p className="text-[11px] text-red-700 leading-relaxed">
                      {loadsKnown && load
                        ? <>They currently hold <b>{load.own} live deal{load.own === 1 ? "" : "s"}</b>{load.split ? <> + <b>{load.split} split{load.split === 1 ? "" : "s"}</b></> : null}{load.unpaidComm ? <> and appear on <b>{load.unpaidComm}</b> deal{load.unpaidComm === 1 ? "" : "s"} with unpaid commission</> : null}. </>
                        : "Their current deal load couldn't be checked — review their pipeline before offboarding. "}
                      This deactivates them (nothing is deleted): they can't log in, deals stay assigned until you
                      reassign them, and while inactive their half of any split won't render on the commission board.
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setOffboardOpen(false)} className="flex-1 py-2 rounded-lg bg-white border border-slate-300 text-xs font-semibold text-slate-600">
                        Keep active
                      </button>
                      <button onClick={handleOffboard} disabled={saving} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                        ⚠ Confirm offboard
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {mode === "edit" && row && !row.is_active && (
              <div className="border-t border-slate-200 pt-4 mt-2">
                <button
                  onClick={handleReactivate}
                  disabled={saving}
                  className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  ↩ Reactivate {row.full_name.split(" ")[0]}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
